import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Divider,
  Chip,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VisibilityIcon from '@mui/icons-material/Visibility';

import JobDetailsCard from '../../waterAuthorityConfig/components/JobDetailsCard';
import SubmissionPdfCard from '../components/SubmissionPdfCard';
import WaterAuthoritySelectionDropdown from '../../waterAuthorityConfig/components/WaterAuthoritySelectionDropdown';

import { BackflowJobGroup } from '../../model';

import PageHeader from '../../../sharedModules/layout/PageHeader';
import axios from '../../../sharedModules/utils/axios.utils';

const WaterAuthoritySubmissionReviewPage: React.FC = () => {
  const { backflowJobGroupId } = useParams<{ backflowJobGroupId: string }>();
  const history = useHistory();

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [backflowJobGroup, setBackflowJobGroup] = useState<BackflowJobGroup | null>(null);
  const [generatingPdfs, setGeneratingPdfs] = useState<boolean>(false);
  const [markingSubmitted, setMarkingSubmitted] = useState<boolean>(false);

  const fetchBackflowJobGroup = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get<any>(`/backflows/backflow_job_groups/${backflowJobGroupId}`);
      const data = response.data.result;
      setBackflowJobGroup(data);
    } catch (err: any) {
      console.error(err);
      setError('Failed to load backflow job group details');
    } finally {
      setLoading(false);
    }
  };

  const generatePdfs = async () => {
    setGeneratingPdfs(true);
    setError('');
    setSuccess('');
    try {
      await axios.post(`/backflows/water_authority_submissions/${backflowJobGroupId}/generate_pdfs`);
      setSuccess('PDFs generated successfully!');
      // Refresh the data
      await fetchBackflowJobGroup();
    } catch (err: any) {
      console.error(err);
      setError('Failed to generate PDFs');
    } finally {
      setGeneratingPdfs(false);
    }
  };

  const markAsSubmitted = async () => {
    setMarkingSubmitted(true);
    setError('');
    setSuccess('');
    try {
      await axios.patch(`/backflows/water_authority_submissions/${backflowJobGroupId}/mark_as_submitted`);
      setSuccess('Submission marked as submitted!');
      // Redirect to submissions list page after short delay to show success message
      setTimeout(() => {
        history.push('/backflow_tests/water_authority_submissions');
      }, 1500);
    } catch (err: any) {
      console.error(err);
      setError('Failed to mark as submitted');
      setMarkingSubmitted(false);
    }
  };

  const viewTemplate = () => {
    if (!backflowJobGroup.pdf_template_url) {
      setError('No template available for this water authority');
      return;
    }

    // Open PDF in a new tab
    window.open(backflowJobGroup.pdf_template_url, '_blank', 'noopener,noreferrer');
  };

  useEffect(() => {
    fetchBackflowJobGroup();
  }, [backflowJobGroupId]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!backflowJobGroup) {
    return (
      <Box p={3}>
        <Alert severity="error">Backflow job group not found</Alert>
      </Box>
    );
  }

  const approvedItems = backflowJobGroup.backflow_job_items?.filter((item) => item.status === 'approved') || [];

  let hoverStyle = {
    bgcolor: '#e9ecef',
    borderColor: '#1976d2',
    transform: 'scale(1.02)',
  };
  if (!backflowJobGroup.pdf_template_url) {
    hoverStyle = {
      bgcolor: '#f5f5f5',
      borderColor: '#f5f5f5',
      transform: 'none',
    };
  }

  return (
    <Box sx={{ flexGrow: 1, bgcolor: '#f5f5f5', minHeight: '100vh', p: 3 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <PageHeader
            mainTitle="Water Authority"
            subTitle={`Set Water Authority - Order #${backflowJobGroup.order_no}`}
            backButtonLabel="WA Submissions List"
            backButtonLink="/backflow_tests/water_authority_submissions"
          />
        </Grid>

        {/* Alerts */}
        {error && (
          <Grid item xs={12}>
            <Alert severity="error" onClose={() => setError('')}>
              {error}
            </Alert>
          </Grid>
        )}

        {success && (
          <Grid item xs={12}>
            <Alert severity="success" onClose={() => setSuccess('')}>
              {success}
            </Alert>
          </Grid>
        )}

        <Grid item xs={12} md={6}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <JobDetailsCard backflowJobGroup={backflowJobGroup} />
            </Grid>
            <Grid item xs={12}>
              <WaterAuthoritySelectionDropdown
                backflowJobGroup={backflowJobGroup}
                fetchBackflowJobGroup={fetchBackflowJobGroup}
              />
            </Grid>
          </Grid>
        </Grid>

        {/* Submission Actions Card */}
        <Grid item xs={12} lg={6}>
          <Card elevation={3} sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight="600">
                Submission Actions
              </Typography>

              <Divider sx={{ mb: 3 }} />

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={generatingPdfs ? <CircularProgress size={20} /> : <PictureAsPdfIcon />}
                    onClick={generatePdfs}
                    disabled={generatingPdfs || approvedItems.length === 0}
                    fullWidth
                    size="large"
                    sx={{ py: 1.5 }}
                  >
                    {generatingPdfs ? 'Generating...' : 'Regenerate PDFs'}
                  </Button>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={markingSubmitted ? <CircularProgress size={20} /> : <CheckCircleIcon />}
                    onClick={markAsSubmitted}
                    disabled={markingSubmitted || approvedItems.length === 0 || backflowJobGroup.status !== 'approved'}
                    fullWidth
                    size="large"
                    sx={{ py: 1.5 }}
                  >
                    {markingSubmitted ? 'Marking...' : 'Mark as Submitted'}
                  </Button>
                </Grid>
              </Grid>

              {(approvedItems.length === 0 || backflowJobGroup.status !== 'approved') && (
                <Box mt={3}>
                  <Alert severity="info">
                    {backflowJobGroup.status !== 'approved'
                      ? `Job group must be in 'approved' status. Current status: ${backflowJobGroup.status || 'unknown'}`
                      : 'Actions require at least one approved item.'}
                  </Alert>
                </Box>
              )}

              {/* PDF Template Preview Section */}
              <Box mt={4}>
                <Divider sx={{ mb: 3 }} />
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  PDF Template Preview
                </Typography>
                <Box
                  onClick={viewTemplate}
                  sx={{
                    mt: 2,
                    p: 3,
                    bgcolor: '#f8f9fa',
                    borderRadius: 2,
                    border: '2px dashed #dee2e6',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 200,
                    cursor: backflowJobGroup.pdf_template_url ? 'pointer' : 'not-allowed',
                    opacity: backflowJobGroup.pdf_template_url ? 1 : 0.6,
                    transition: 'all 0.3s ease',
                    '&:hover': hoverStyle,
                  }}
                >
                  <PictureAsPdfIcon sx={{ fontSize: 64, color: '#d32f2f', mb: 2 }} />
                  <Typography variant="body1" fontWeight="500" gutterBottom>
                    {backflowJobGroup.water_authority_name || 'Water Authority'} Submission Form
                  </Typography>
                  <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 2 }}>
                    {backflowJobGroup.pdf_template_url
                      ? 'Click to view template'
                      : 'No template available for this water authority'}
                  </Typography>

                  {backflowJobGroup.pdf_template_url && (
                    <Chip icon={<VisibilityIcon />} label="View Template" color="primary" size="small" sx={{ mt: 1 }} />
                  )}

                  {approvedItems.length > 0 && (
                    <Box mt={2}>
                      <Chip
                        label={`${approvedItems.length} item${approvedItems.length !== 1 ? 's' : ''} ready`}
                        color="success"
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* PDF List Section Header */}
        <Grid item xs={12}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h5" fontWeight="700">
              Water Authority Submission PDFs
            </Typography>
            <Chip label={`${approvedItems.length} Items`} color="primary" />
          </Box>
        </Grid>

        {/* PDF Cards */}
        {approvedItems.length === 0 ? (
          <Grid item xs={12}>
            <Card elevation={1}>
              <CardContent>
                <Alert severity="info">
                  No approved items found for this job group. Items must be approved before submission PDFs can be
                  generated.
                </Alert>
              </CardContent>
            </Card>
          </Grid>
        ) : (
          approvedItems.map((item) => (
            <Grid item xs={12} md={6} key={item.id}>
              <SubmissionPdfCard item={item} />
            </Grid>
          ))
        )}
      </Grid>
    </Box>
  );
};

export default WaterAuthoritySubmissionReviewPage;
